﻿import { useEffect, useState, useRef } from 'react';
import { Button, Badge, Form, ButtonGroup, Spinner as BsSpinner } from 'react-bootstrap';
import { FiDownload, FiTrash2, FiSave, FiFile, FiImage, FiFileText } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useCustomerDetail } from '@/hooks/useCustomerDetail';
import CustomerDetail from './CustomerDetail';
import { config } from '@/config/env';
import { useHask } from '@/hooks/useHask';
import { useToast } from '@/components/common/Toast';
import SidePanel from '@/components/common/SidePanel';
import QuestionForm from './QuestionForm';
import type { Visit } from '@/hooks/useVisits';

interface RisposteResponse {
  risposte: {
    id: string;
    risposte: Record<string, string>;
    attributi: { area: string; categoria: string; codice: string };
    mostrabtnelimina?: boolean;
  };
  allegati?: { idfile: string; nomefile: string; nomefileoriginario?: string; dimensione?: number }[];
  usercreate?: string;
  userupdate?: string;
}

interface Props {
  visit: Visit | null;
  show: boolean;
  onHide: () => void;
  onDelete: (visit: Visit) => void;
  onDownload: (visit: Visit) => void;
  onSaved?: () => void;
}

function getFileIcon(filename: string) {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const style = { fontSize: 32 };
  switch (ext) {
    case 'pdf':
      return <FiFileText style={{ ...style, color: '#dc3545' }} />;
    case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': case 'svg':
      return <FiImage style={{ ...style, color: '#0d6efd' }} />;
    case 'doc': case 'docx':
      return <FiFileText style={{ ...style, color: '#0d6efd' }} />;
    case 'xls': case 'xlsx': case 'csv':
      return <FiFileText style={{ ...style, color: '#198754' }} />;
    default:
      return <FiFile style={style} className="text-muted" />;
  }
}

export default function VisitDetailPanel({ visit, show, onHide, onDelete, onDownload, onSaved }: Props) {
  const { t } = useTranslation('visits');
  const { t: tc } = useTranslation();
  const { showToast } = useToast();
  const { customer, fetchCustomer, clear: clearCustomer } = useCustomerDetail();
  const { questions, loading: haskLoading, loadQuestions, setAnswer, getAnswers, validate, save, resetAnswers } = useHask();
  const [detail, setDetail] = useState<RisposteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const originalAnswersRef = useRef<Record<string, string>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [activeVersion, setActiveVersion] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (show && visit) {
      setActiveVersion(visit.versione);
      loadDetail(visit.id, visit.versione);
      setFiles([]);
    } else {
      setDetail(null);
      resetAnswers();
      clearCustomer();
    }
  }, [show, visit]);

  const loadDetail = async (id: string, versione: number) => {
    setLoading(true);
    try {
      const data = await api.get<RisposteResponse>(
        'lmeet/risposte-questionario/' + id + '/' + versione
      );
      setDetail(data);
      // Fetch customer detail
      if (visit) fetchCustomer(visit.codcliente);

      if (data?.risposte) {
        const { attributi, risposte } = data.risposte;
        resetAnswers();
        if (risposte) {
          originalAnswersRef.current = { ...(risposte as Record<string, string>) };
          Object.entries(risposte).forEach(([key, val]) => {
            setAnswer(key, val as string);
          });
        }
        setHasChanges(false);
        if (attributi) {
          localStorage.setItem('hask_codarea', attributi.area);
          localStorage.setItem('hask_codice', attributi.codice);
          await loadQuestions(attributi.area, attributi.codice, attributi.categoria || '*');
        }
      }
    } catch (err) {
      console.error('Error loading visit detail:', err);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionChange = (ver: number) => {
    if (!visit || ver === activeVersion) return;
    setActiveVersion(ver);
    loadDetail(visit.id, ver);
  };

  const handleSave = async () => {
    if (!visit || !detail) return;
    setSaving(true);
    try {
      await save(visit.codazienda, visit.codcliente, files, detail.risposte?.id || visit.id);
      showToast(tc('actions.save'), t('saveSuccess'), 'success');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setFiles([]);
      onHide();
      if (onSaved) onSaved();
    } catch {
      showToast('Errore', 'Errore durante il salvataggio', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAttachment = async (idfile: string, nomefile: string) => {
    try {
      const base = config.WSREST_TABATA.replace(/\/+$/, '');
      const token = sessionStorage.getItem('mT');
      const res = await fetch(base + '/lmeet/risposte-questionario/allegato/' + idfile, {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'allegato-' + nomefile;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Attachment download error:', err);
    }
  };

  const isLoading = loading || haskLoading;
  const canDelete = detail?.risposte?.mostrabtnelimina;
  const maxVersion = visit?.versione || 1;

  const footer = visit ? (
    <>
      {canDelete && (
        <Button variant="danger" className="flex-fill" onClick={() => { onHide(); onDelete(visit); }}>
          <FiTrash2 className="me-1" /> {tc('actions.delete')}
        </Button>
      )}
      <Button variant="primary" className="flex-fill" onClick={handleSave} disabled={saving || !hasChanges}>
        {saving ? <BsSpinner animation="border" size="sm" className="me-1" /> : <FiSave className="me-1" />}
        {t('saveChanges')}
      </Button>
    </>
  ) : undefined;

  return (
    <SidePanel show={show} onHide={onHide} footer={footer} width={1200}>
      {/* Header with version selector */}
      {visit && (
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <span style={{ fontSize: 11 }} className="text-muted">
                ID Visita {visit.id}
              </span>
              <span style={{ fontSize: 13 }}>
                Versione <Badge bg="success">{activeVersion}</Badge>
              </span>
              {maxVersion > 1 && (
                <ButtonGroup size="sm">
                  {Array.from({ length: maxVersion }, (_, i) => i + 1).map(ver => (
                    <Button
                      key={ver}
                      variant={ver === activeVersion ? 'outline-primary' : 'outline-secondary'}
                      onClick={() => handleVersionChange(ver)}
                      style={{ minWidth: 32, fontSize: 12 }}
                    >
                      {ver}
                    </Button>
                  ))}
                </ButtonGroup>
              )}
            </div>
            <div className="text-end" style={{ fontSize: 11 }}>
              {detail?.usercreate && (
                <div className="text-muted">Creato da <strong>{detail.usercreate}</strong></div>
              )}
              {activeVersion > 1 && detail?.userupdate && (
                <div className="text-muted">Modificato da <strong>{detail.userupdate}</strong></div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Customer detail card */}
      {customer && <CustomerDetail customer={customer} />}

      {isLoading && (
        <div className="text-center py-5">
          <BsSpinner animation="border" className="spinner-primary" />
        </div>
      )}

      {!isLoading && detail && (
        <>
          <hr className="mt-0" />

          {questions.length > 0 && (
            <QuestionForm
              questions={questions}
              answers={getAnswers()}
              onAnswer={(codattr, value) => {
                setAnswer(codattr, value);
                if (value !== (originalAnswersRef.current[codattr] || '')) setHasChanges(true);
              }}
              originalAnswers={originalAnswersRef.current}
            />
          )}

          <hr className="my-3" />

          {detail.allegati && detail.allegati.length > 0 && (
            <div className="mb-3">
              <label className="form-label fs-xs fw-medium">Allegati</label>
              <div className="row g-2">
                {detail.allegati.map(att => (
                  <div key={att.idfile} className="col-2"
                    onClick={() => handleDownloadAttachment(att.idfile, att.nomefileoriginario || att.nomefile)}
                    style={{ cursor: 'pointer' }}>
                    <div className="card h-100 border-0 shadow-sm text-center p-3 d-flex align-items-center justify-content-center">
                      {getFileIcon(att.nomefileoriginario || att.nomefile)}
                      <p className="mb-0 pt-2" style={{ fontSize: 11, wordBreak: 'break-word' }}>
                        {att.nomefileoriginario || att.nomefile}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fs-xs fw-medium">Aggiungi allegati</Form.Label>
            <Form.Control type="file" multiple ref={fileInputRef} size="sm"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files ? Array.from(e.target.files) : []; setFiles(f); if (f.length > 0) setHasChanges(true); }} />
            {files.length > 0 && (
              <Form.Text>{t('filesSelected', { count: files.length })}</Form.Text>
            )}
          </Form.Group>
        </>
      )}

      {!isLoading && !detail && visit && (
        <p className="text-muted text-center py-4">Errore nel caricamento del dettaglio</p>
      )}
    </SidePanel>
  );
}











